import { useState } from "react";

import {
  makeGetApiCall,
  makePostApiCall,
  makePutApiCall,
} from "../../shared/makeApiCall";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import ProgressModal from "../../components/modals/ProgressModal";
import { generateUniqueNumber } from "../../shared/utility";

import ManufacturedItemInfo from "../../components/forms/manufacturedProducts/ManufacturedItemInfo";
import RemoveWipItemByIdForm from "../../components/forms/wips/RemoveWipItemByIdForm";
import DeleteWipOutputItemByIdForm from "../../components/forms/wips/DeleteWipOutputItemByIdForm";
import DeleteWipOutputItemsMultipleForm from "../../components/forms/wips/DeleteWipOutputItemsMultipleForm";
import AddPriceGroupModal from "../../components/forms/wips/AddPriceGroupModal";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";

export function WipDetailsViewModel(logout, wipId, addSnackbar) {
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [roomOptions, setRoomOptions] = useState([]);
  const [removeReasonFromWipOptions, setRemoveReasonFromWipOptions] = useState(
    []
  );
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [wip, setWip] = useState(null);
  const [outgoingSelectedItems, setOutgoingSelectedItems] = useState([]);
  const [outgoingDeleteSelectedItems, setOutgoingDeleteSelectedItems] = useState(
    []
  );
  const [incomingSelectedItems, setIncomingSelectedItems] = useState([]);
  const [rowDetails, setRowDetails] = useState({}); // { [rowId]: detailData }
  const [loadingRows, setLoadingRows] = useState({}); // { [rowId]: boolean }
  const [subRowDetails, setSubRowDetails] = useState({}); // { [rowId]: detailData }
  const [subLoadingRows, setSubLoadingRows] = useState({}); // { [rowId]: boolean }
  const [lastRow, setLastRow] = useState(null);
  const [outgoingRowDetails, setOutgoingRowDetails] = useState({}); // { [rowId]: detailData }
  const [outgoingLoadingRows, setOutgoingLoadingRows] = useState({}); // { [rowId]: boolean }
  const [outgoingSubRowDetails, setOutgoingSubRowDetails] = useState({}); // { [rowId]: detailData }
  const [outgoingSubLoadingRows, setOutgoingSubLoadingRows] = useState({}); // { [rowId]: boolean }
  const [outgoingLastRow, setOutgoingLastRow] = useState(null);
  const [outgoingParentRow, setOutgoingParentRow] = useState(null);

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title ? title : "Error"}
        />
      ),
      open: true,
    });
  };

  const getWipDetails = async function () {
    let callResult = await makeGetApiCall(`wips/${wipId}`);

    if (callResult.success) {
      setWip(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch Wip information");
    }
    return callResult;
  };

  const getRoomOptionsForDropdown = async function () {
    let callResult = await makeGetApiCall(`roomsForDropdown`);

    if (callResult.success) {
      setRoomOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch rooms");
    }
  };

  const updateWip = async function (payload) {
    let callResult = await makePutApiCall(`wips/${wip.id}`, {
      ...payload,
    });

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(generateUniqueNumber(), "WIP updated", SnackbarType.Success)
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const onAddItemToWipByBarcode = async function (scannedBarcode) {
    let callResult = await makePostApiCall("wipItems/add", {
      wip_id: wip.id,
      barcode: scannedBarcode,
    });

    if (callResult.success) {
      setWip({
        ...wip,
        incoming_items: callResult.data.items,
        incoming_info: callResult.data.info,
      });
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Item added to WIP",
          SnackbarType.Success
        )
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const onRemoveItemWipByBarcode = async function (payload) {
    console.log("payload", payload);

    let callResult = await makePostApiCall("wipItems/remove", {
      wip_id: wip.id,
      ...payload,
    });

    if (callResult.success) {
      setWip({
        ...wip,
        incoming_items: callResult.data.incoming_items.items,
        incoming_info: callResult.data.incoming_items.info,
      });
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Item removed from WIP",
          SnackbarType.Success
        )
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const getRemoveReasonsOptionsForDropdown = async function () {
    let callResult = await makeGetApiCall(`reasonCodesForDropdown`);

    if (callResult.success) {
      setRemoveReasonFromWipOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch reasons to remove");
    }
  };

  const onGetWipItemByBarcode = async function (barcode) {
    let callResult = await makeGetApiCall(`wipItems/barcode/${barcode}`);

    if (callResult.success) {
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch barcode information");
    }
    return callResult;
  };

  const openSlideForManufacturedItemDetails = async function (
    manProdId,
    manProdPalletId
  ) {
    var callResult = { success: false };
    if (manProdPalletId) {
      callResult = await makeGetApiCall(
        `manufacturedPallets/${manProdPalletId}`
      );
    } else {
      callResult = await makeGetApiCall(`manufacturedProducts/${manProdId}`);
    }
    if (callResult.success) {
      setSlideOver({
        childComponent: (
          <ManufacturedItemInfo item={callResult.data} setOpen={setSlideOver} />
        ),
        open: true,
      });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch manufactured item");
    }
  };

  const onRemoveMultipleIncomingItems = async function () {
    const items = incomingSelectedItems.map((row) => ({ barcode: row.barcode }));
    let callResult = await makePostApiCall("wipItems/removeMultiple", {
      wip_id: wip.id,
      items,
    });

    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      setWip({
        ...wip,
        incoming_items: callResult.data.incoming_items.items,
        incoming_info: callResult.data.incoming_items.info,
      });
      setIncomingSelectedItems([]);
      setRowDetails({});
      setSubRowDetails({});
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Items returned from WIP",
          SnackbarType.Success
        )
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const askReturnSelectedIncomingItems = () => {
    if (!incomingSelectedItems?.length) {
      showErrorModal("Please select at least one item to return.");
      return;
    }
    setEmptyModelOpen({
      open: true,
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Return"
          hideReason={true}
          message="Return selected items from WIP? This is a full return."
          confirmAction={onRemoveMultipleIncomingItems}
          setOpen={setEmptyModelOpen}
        />
      ),
    });
  };

  const onAskReturnIncomingItem = (wipItem) => {
    setEmptyModelOpen({
      open: true,
      childComponent: (
        <RemoveWipItemByIdForm
        wip={wip}
          onRemoveItemWipByBarcode={onRemoveItemWipByBarcode}
          removeReasonFromWipOptions={removeReasonFromWipOptions}
          setEmptyModelOpen={setEmptyModelOpen}
          item={wipItem}
        />
      ),
    });
  };

  const onDeleteWipOutputItemByBarcode = async function (
    deletedProduct,
    reason
  ) {
    let callResult = await makePostApiCall(
      `manufactureProducts/deleteByBarcode/${deletedProduct.barcode}`,
      {
        reason_id: reason.value,
        reason_name: reason.label,
        wip_id: wipId,
      }
    );

    if (callResult.success) {
      setWip({
        ...wip,
        outgoing_items: callResult.data.outgoing_items.items,
      });
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Carton deleted from outgoing WIP",
          SnackbarType.Success
        )
      );

      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });

      if (outgoingParentRow) {
        fetchOutgoingRowDetails(outgoingParentRow, false);
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const onDeleteMultipleWipOutputItems = async function (reason) {
    const items = outgoingDeleteSelectedItems.map((row) => ({
      barcode: row.barcode,
    }));
    let callResult = await makePostApiCall(
      "manufactureProducts/deleteByBarcodeMultiple",
      {
        wip_id: wipId,
        reason: reason.label,
        items,
      }
    );

    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      setWip({
        ...wip,
        outgoing_items: callResult.data.outgoing_items.items,
        outgoing_info: callResult.data.outgoing_items.info,
      });
      setOutgoingDeleteSelectedItems([]);
      setOutgoingRowDetails({});
      setOutgoingSubRowDetails({});
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message || "Items deleted from outgoing WIP",
          SnackbarType.Success
        )
      );

      if (outgoingParentRow) {
        fetchOutgoingRowDetails(outgoingParentRow, false);
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const askDeleteSelectedOutgoingItems = () => {
    if (!outgoingDeleteSelectedItems?.length) {
      showErrorModal("Please select at least one item to delete.");
      return;
    }
    setEmptyModelOpen({
      open: true,
      childComponent: (
        <DeleteWipOutputItemsMultipleForm
          itemCount={outgoingDeleteSelectedItems.length}
          onDeleteMultiple={onDeleteMultipleWipOutputItems}
          removeReasonFromWipOptions={removeReasonFromWipOptions}
          setEmptyModelOpen={setEmptyModelOpen}
        />
      ),
    });
  };

  const onAskDeleteWipOutputItem = (wipItem) => {
    setEmptyModelOpen({
      open: true,
      childComponent: (
        <DeleteWipOutputItemByIdForm
          onDeleteWipOutputItemByBarcode={(reason) =>
            onDeleteWipOutputItemByBarcode(wipItem, reason)
          }
          removeReasonFromWipOptions={removeReasonFromWipOptions}
          setEmptyModelOpen={setEmptyModelOpen}
          item={wipItem}
        />
      ),
    });
  };

  const onCreatePriceGroup = async function (costPercentage) {
    if (outgoingSelectedItems.length < 1) {
      showErrorModal("Please select outgoing items");
    } else {
      // Close the input modal and show progress modal
      setEmptyModelOpen({
        open: true,
        childComponent: <ProgressModal message="Creating price group..." />,
      });

      let callResult = await makePostApiCall(`updateOutputItemCostPercentage`, {
        wip_id: wipId,
        production_cost_percentage: costPercentage,
        items: outgoingSelectedItems,
      });

      // Close progress modal
      setEmptyModelOpen({ open: false, childComponent: null });

      if (callResult.success) {
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            "Price group created",
            SnackbarType.Success
          )
        );
        setWip({
          ...wip,
          outgoing_items: callResult.data.items,
          outgoing_info: callResult.data.info,
        });
        setOutgoingSelectedItems([]);
        if (outgoingParentRow) {
          fetchOutgoingRowDetails(outgoingParentRow, false);
        }
      } else {
        if (callResult.errorStatus === 401) {
          logout();
        }
        showErrorModal(callResult.data.message);
      }
      return callResult;
    }
  };

  const openAddPriceGroupModal = () => {
    setEmptyModelOpen({
      open: true,
      childComponent: (
        <AddPriceGroupModal
          items={outgoingSelectedItems}
          createPriceGroup={onCreatePriceGroup}
          setEmptyModelOpen={setEmptyModelOpen}
        />
      ),
    });
  };

  const calculateCostForWipOutgoingItems = async function () {
    // Show progress modal
    setEmptyModelOpen({
      open: true,
      childComponent: <ProgressModal message="Calculating costs..." />,
    });

    let callResult = await makePostApiCall(`calculateCostForWipOutgoingItems`, {
      wip_id: wipId,
    });

    // Close progress modal
    setEmptyModelOpen({ open: false, childComponent: null });

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Costs calculated",
          SnackbarType.Success
        )
      );
      setWip({
        ...wip,
        outgoing_items: callResult.data.items,
        outgoing_info: callResult.data.info,
      });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const printWipDetailsReport = async function () {
    let callResult = await makePostApiCall(`generateWipDetailsReport/${wipId}`);

    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const printWipSummaryReport = async function () {
    let callResult = await makePostApiCall(`generateWipSummaryReport/${wipId}`);

    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const printWipDerivativesReport = async function () {
    let callResult = await makePostApiCall(
      `generateWipDerivativesReport/${wipId}`
    );

    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const fetchRowDetails = async (rowData) => {
    setLoadingRows((prev) => ({ ...prev, [rowData.unique_id]: true }));

    let callResult = await makeGetApiCall(
      `wipItems/incoming/${wipId}/${rowData.plu}/${rowData.lot_number}/${rowData.unique_id}/${rowData.date}`
    );
    if (callResult.success) {
      setRowDetails({ ...rowDetails, [rowData.unique_id]: callResult.data });
      setLoadingRows({ ...loadingRows, [rowData.unique_id]: false });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const fetchSubRowDetails = async (rowData) => {
    setLastRow(rowData);
    setSubLoadingRows((prev) => ({ ...prev, [rowData.unique_id]: true }));

    let callResult = await makeGetApiCall(
      `wipItems/incoming/pallets/${rowData.pallet_id}`
    );
    if (callResult.success) {
      setSubRowDetails({
        ...subRowDetails,
        [rowData.unique_id]: callResult.data,
      });
      setSubLoadingRows({ ...subLoadingRows, [rowData.unique_id]: false });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const fetchOutgoingRowDetails = async (rowData, showLoading = true) => {
    setOutgoingParentRow(rowData);
    if (showLoading) {
      setOutgoingLoadingRows((prev) => ({
        ...prev,
        [rowData.unique_id]: true,
      }));
    }

    let callResult = await makeGetApiCall(
      `wipItems/outgoing/${wipId}/${rowData.plu}/${rowData.lot_number}`
    );
    if (callResult.success) {
      setOutgoingRowDetails({
        ...outgoingRowDetails,
        [rowData.unique_id]: callResult.data,
      });
      setOutgoingLoadingRows({
        ...outgoingLoadingRows,
        [rowData.unique_id]: false,
      });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const fetchOutgoingSubRowDetails = async (rowData) => {
    setOutgoingLastRow(rowData);
    setOutgoingSubLoadingRows((prev) => ({
      ...prev,
      [rowData.unique_id]: true,
    }));

    let callResult = await makeGetApiCall(
      `wipItems/outgoing/pallets/${rowData.pallet_id}`
    );
    if (callResult.success) {
      setOutgoingSubRowDetails({
        ...outgoingSubRowDetails,
        [rowData.unique_id]: callResult.data,
      });
      setOutgoingSubLoadingRows({
        ...outgoingSubLoadingRows,
        [rowData.unique_id]: false,
      });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const onWipItemTotalCostEditComplete = async function (payload) {
    let callResult = await makePostApiCall(
      `updateLegacyItemTotalCostFromWip`,
      payload
    );
    if (callResult.success) {
      getWipDetails();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    return callResult;
  };

  return {
    emptyModal,
    setEmptyModelOpen,
    roomOptions,
    getRoomOptionsForDropdown,
    wip,
    onAddItemToWipByBarcode,
    onRemoveItemWipByBarcode,
    removeReasonFromWipOptions,
    getRemoveReasonsOptionsForDropdown,
    onGetWipItemByBarcode,
    updateWip,
    getWipDetails,
    openSlideForManufacturedItemDetails,
    slideOver,
    setSlideOver,
    onAskReturnIncomingItem,
    incomingSelectedItems,
    setIncomingSelectedItems,
    askReturnSelectedIncomingItems,
    onAskDeleteWipOutputItem,
    openAddPriceGroupModal,
    setOutgoingSelectedItems,
    outgoingSelectedItems,
    outgoingDeleteSelectedItems,
    setOutgoingDeleteSelectedItems,
    askDeleteSelectedOutgoingItems,
    calculateCostForWipOutgoingItems,
    printWipDetailsReport,
    printWipSummaryReport,
    printWipDerivativesReport,
    showErrorModal,
    loadingRows,
    rowDetails,
    fetchSubRowDetails,
    subRowDetails,
    subLoadingRows,
    fetchRowDetails,
    fetchOutgoingRowDetails,
    outgoingLoadingRows,
    outgoingRowDetails,
    fetchOutgoingSubRowDetails,
    outgoingSubRowDetails,
    outgoingSubLoadingRows,
    setOutgoingRowDetails,
    setOutgoingSubRowDetails,
    onWipItemTotalCostEditComplete,
  };
}
